import type { ColorBlockColor } from "@repo/ui/color-block";

/**
 * Brand colour palette used across the web app.
 *
 * `ColorBlockColor` is the CMS-driven palette (mirrors apps/studio/utils/design-colors.ts).
 * `BrandColor` widens that with web-only tokens (e.g. `afternoon` used in the image-shape grid).
 *
 * Single source of truth: `brandColors`. Prefer the `bgClass` / `bgClass10` Tailwind tokens
 * over `hex` — only reach for `hex` when an inline style is unavoidable (e.g. building an
 * `rgba()`-style colour with non-standard alpha).
 *
 * Tailwind-class strings are written as full literals so the Tailwind v4 scanner can detect them.
 */
export type BrandColor = ColorBlockColor | "afternoon";

interface BrandColorTokens {
  /** Tailwind `bg-*` utility class. */
  bgClass: string;
  /** Tailwind `bg-*` utility class at 10% opacity. */
  bgClass10: string;
  /** Hex value — matches the Tailwind token in packages/tailwind-config/colors.css. Use only when inline style is required. */
  hex: string;
  /** True when the background is dark enough to require light text. */
  isDark: boolean;
}

export const brandColors: Record<BrandColor, BrandColorTokens> = {
  "bright-blue": {
    bgClass: "bg-bright-blue-500",
    bgClass10: "bg-bright-blue-500/10",
    hex: "#5375CF",
    isDark: true,
  },
  "light-green": {
    bgClass: "bg-light-green-300",
    bgClass10: "bg-light-green-300/10",
    hex: "#9BD7D2",
    isDark: false,
  },
  turquoise: {
    bgClass: "bg-turquoise-600",
    bgClass10: "bg-turquoise-600/10",
    hex: "#00A399",
    isDark: true,
  },
  peach: {
    bgClass: "bg-peach",
    bgClass10: "bg-peach/10",
    hex: "#FF946A",
    isDark: false,
  },
  avocado: {
    bgClass: "bg-avocado-300",
    bgClass10: "bg-avocado-300/10",
    hex: "#C6E57D",
    isDark: false,
  },
  "sky-blue": {
    bgClass: "bg-sky-400",
    bgClass10: "bg-sky-400/10",
    hex: "#90A8ED",
    isDark: false,
  },
  orange: {
    bgClass: "bg-orange",
    bgClass10: "bg-orange/10",
    hex: "#EDA340",
    isDark: false,
  },
  sea: {
    bgClass: "bg-sea-700",
    bgClass10: "bg-sea-700/10",
    hex: "#008AAA",
    isDark: true,
  },
  mint: {
    bgClass: "bg-mint-400",
    bgClass10: "bg-mint-400/10",
    hex: "#68C187",
    isDark: false,
  },
  yellow: {
    bgClass: "bg-sunshine-yellow-500",
    bgClass10: "bg-sunshine-yellow-500/10",
    hex: "#FFC627",
    isDark: false,
  },
  cobalt: {
    bgClass: "bg-cobalt-500",
    bgClass10: "bg-cobalt-500/10",
    hex: "#3B72FF",
    isDark: true,
  },
  ocean: {
    bgClass: "bg-open-water-600",
    bgClass10: "bg-open-water-600/10",
    hex: "#1A3C82",
    isDark: true,
  },
  afternoon: {
    bgClass: "bg-afternoon-200",
    bgClass10: "bg-afternoon-200/10",
    hex: "#B8DBF7",
    isDark: false,
  },
};

const pluck = <K extends keyof BrandColorTokens>(
  key: K,
): Record<BrandColor, BrandColorTokens[K]> =>
  Object.fromEntries(
    (Object.entries(brandColors) as [BrandColor, BrandColorTokens][]).map(
      ([colour, tokens]) => [colour, tokens[key]],
    ),
  ) as Record<BrandColor, BrandColorTokens[K]>;

/** Tailwind `bg-*` class for each brand colour. */
export const brandColorBgClass = pluck("bgClass");

/** Tailwind `bg-*` class at 10% opacity for each brand colour. */
export const brandColorBgClass10 = pluck("bgClass10");

/** Hex value for each brand colour. Prefer `brandColorBgClass` unless an inline style is required. */
export const brandColorHex = pluck("hex");

/**
 * Returns true if the brand colour has a dark background requiring light text.
 * Accepts unknown strings defensively (CMS values can drift out of the typed union) and
 * defaults to `true` so unknown backgrounds get light text — matches the prior set-based behaviour.
 */
export function isDarkBackground(color: string | null | undefined): boolean {
  if (!color) return true;
  return brandColors[color as BrandColor]?.isDark ?? true;
}

/**
 * Resolve a raw CMS colour string to the narrow `ColorBlockColor` union.
 * The generated schema type for a `colourBlockField` is `string`, so callers
 * previously had to cast — this helper validates against `brandColors` and
 * falls back to the caller-supplied default for unknown or web-only values.
 */
export function resolveBrandColour(
  value: string | undefined | null,
  fallback: ColorBlockColor,
): ColorBlockColor {
  if (!value) return fallback;
  return value in brandColors && value !== "afternoon"
    ? (value as ColorBlockColor)
    : fallback;
}
